import { z } from "zod";
import { lotPolicySchema } from "./settings.js";

export const channelRoleSchema = z.enum(["signal", "system", "trades"]);
export type ChannelRole = z.infer<typeof channelRoleSchema>;

/**
 * Per-channel hints fed to the parser. Signal writers are inconsistent about instrument
 * names ("GOLD", "XAU", "gold spot") and some channels have habits worth telling the
 * model about in plain language.
 */
export const symbolHintsSchema = z.object({
  /** Raw text the channel uses -> our canonical symbol, e.g. { "GOLD": "XAUUSD" }. */
  aliases: z.record(z.string(), z.string()).default({}),
  /** Free-text quirks, e.g. "quotes gold without the decimal point". */
  notes: z.string().max(2000).default(""),
});
export type SymbolHints = z.infer<typeof symbolHintsSchema>;

/**
 * Widens the signal's stop and targets before we submit them. Our broker's spread is
 * usually worse than the signaller's, so their exact levels can stop us out on noise
 * they never saw. Positive values move the stop further away and pull targets nearer.
 */
export const slTpPaddingSchema = z.object({
  defaultSlPips: z.number().min(0).max(1000).default(0),
  defaultTpPips: z.number().min(0).max(1000).default(0),
  /** Per-symbol overrides, e.g. { "XAUUSD": { slPips: 5, tpPips: 2 } }. */
  overrides: z
    .record(
      z.string(),
      z.object({
        slPips: z.number().min(0).max(1000),
        tpPips: z.number().min(0).max(1000),
      }),
    )
    .default({}),
});
export type SlTpPadding = z.infer<typeof slTpPaddingSchema>;

export const channelLotPolicyOverrideSchema = lotPolicySchema.nullable();

export const DEFAULT_SYMBOL_HINTS: SymbolHints = { aliases: {}, notes: "" };
export const DEFAULT_SL_TP_PADDING: SlTpPadding = {
  defaultSlPips: 0,
  defaultTpPips: 0,
  overrides: {},
};
